import { motion } from 'framer-motion';
import { X, Check, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

interface ComparisonItem {
  problem: string;
  solution: string;
}

interface BeforeAfterComparisonProps {
  items: ComparisonItem[];
}

const BeforeAfterComparison = ({ items }: BeforeAfterComparisonProps) => {
  const { t } = useTranslation();
  return (
    <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
      {/* Before - Traditional */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Card className="p-8 h-full bg-gradient-to-br from-destructive/5 to-destructive/10 border-destructive/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
              <X className="w-6 h-6 text-destructive" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{t('comparison.before')}</h3>
              <p className="text-sm text-muted-foreground">{t('comparison.before_subtitle')}</p>
            </div>
          </div>
          <ul className="space-y-4">
            {items.map((item, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <X className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{item.problem}</span>
              </motion.li>
            ))}
          </ul>
        </Card>
      </motion.div>

      {/* After - With Holionix */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Card className="p-8 h-full bg-gradient-to-br from-gradient-cyan/10 via-gradient-purple/10 to-gradient-pink/10 border-accent/20 relative overflow-hidden">
          <div className="absolute top-4 right-4">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="gradient-brand text-white text-xs font-bold px-3 py-1 rounded-full"
            >
              {t('comparison.badge')}
            </motion.div>
          </div>
          
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl gradient-brand flex items-center justify-center">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{t('comparison.after')}</h3>
              <p className="text-sm text-accent">{t('comparison.after_subtitle')}</p>
            </div>
          </div>
          <ul className="space-y-4">
            {items.map((item, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                <span className="font-medium">{item.solution}</span>
              </motion.li>
            ))}
          </ul>
        </Card>
      </motion.div>
    </div>
  );
};

export default BeforeAfterComparison;
